// =============================================
// Query Fan-Out Service
// v4.0: 3-call pipeline — Intent+Areas → PAA Assignment → MICRO/MACRO Classification
// Closed intent taxonomy, role-driven primary intent, hard limits
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { parseLLMJSON, getLLMProvider, getStageConfig } from "@/services/llm";
import { loadPrompt, fillTemplate } from "@/services/llm/promptLoader";
import { getPeopleAlsoAsk } from "@/services/dataForSeoService";
import { normalizeForDedup, jaccardSimilarity } from "./textUtils";
import { logPublic, logDebug, logVerbose, logError } from "./contentLogger";

// =============================================
// v8 KG-anchored area generation — helpers
// =============================================

// v8: KG context fetch — entities (top 15 strength>=30) + measurables + facts + relations
export interface KgFanOutContext {
  kgEntitiesSummary: string;
  kgMeasurables: string;
  kgFacts: string;
  kgRelations: string;
  kgEntityNamesPool: string[];
  eavValuesPool: string[];
  measurableSummaries: string[];
}

async function fetchKgContextForFanOut(nodeId: string): Promise<KgFanOutContext> {
  const { data: kgRow } = await (supabase as any)
    .from("content_knowledge_graphs")
    .select("kg_data")
    .eq("node_id", nodeId)
    .maybeSingle();

  const empty = {
    kgEntitiesSummary: "(brak)",
    kgMeasurables: "(brak)",
    kgFacts: "(brak)",
    kgRelations: "(brak)",
    kgEntityNamesPool: [] as string[],
    eavValuesPool: [] as string[],
    measurableSummaries: [] as string[],
  };
  if (!kgRow?.kg_data) return empty;
  const kg = kgRow.kg_data;

  // Top 15 entities — strength >= 30, skip ent_map_* (legacy map-level)
  const entities = (kg.entities || [])
    .filter((e: any) => e.entity_id && !String(e.entity_id).startsWith("ent_map_") && (e.strength || 0) >= 30)
    .sort((a: any, b: any) => (b.strength || 0) - (a.strength || 0))
    .slice(0, 15);
  const kgEntitiesSummary = entities.length === 0
    ? "(brak)"
    : entities.map((e: any) =>
        `- ${e.entity_name} (${e.type || "?"}, strength=${e.strength || 0})${e.description ? ` — ${String(e.description).substring(0, 200)}` : ""}`,
      ).join("\n");
  const kgEntityNamesPool = entities.map((e: any) => e.entity_name);

  // Top 8 measurables
  const measurables = (kg.measurables || []).slice(0, 8);
  const kgMeasurables = measurables.length === 0
    ? "(brak)"
    : measurables.map((m: any) =>
        `- ${m.measurable}: ${m.value}${m.unit ? ` ${m.unit}` : ""}`,
      ).join("\n");
  const measurableSummaries = measurables.map((m: any) =>
    `${m.measurable}: ${m.value}${m.unit ? ` ${m.unit}` : ""}`,
  );

  // Top 6 high-priority OR high-confidence facts + entity-id dereference
  const facts = (kg.facts || [])
    .filter((f: any) => f.priority === "high" || (f.confidence || 0) >= 0.85)
    .slice(0, 6);
  const entityIdToName = new Map<string, string>(
    entities.map((e: any) => [e.entity_id as string, e.entity_name as string]),
  );
  const kgFacts = facts.length === 0
    ? "(brak)"
    : facts.map((f: any) => {
        const mentionedNames = (f.entities_mentioned || [])
          .map((eid: string) => entityIdToName.get(eid))
          .filter(Boolean)
          .join(", ");
        return `- ${f.fact}${mentionedNames ? ` [linked entities: ${mentionedNames}]` : ""}`;
      }).join("\n");

  // Top 8 relations (already dereferenced pairs in entity_id2text)
  const relations = (kg.entities_relationships || []).slice(0, 8);
  const kgRelations = relations.length === 0
    ? "(brak)"
    : relations.map((r: any) => {
        const pair = (r.entity_id2text || []).join(" → ");
        return `- ${pair} (${r.predicate || "?"})${r.description ? `: ${r.description}` : ""}`;
      }).join("\n");

  return { kgEntitiesSummary, kgMeasurables, kgFacts, kgRelations, kgEntityNamesPool, eavValuesPool: [], measurableSummaries };
}

// v8: EAV per primary_entity_id fallback — used ONLY when ctxEav empty.
// Fixes v7 BUG: previous code did map_id-only fetch returning unrelated entity attributes.
async function fetchEavPerPrimaryEntityFallback(nodeId: string, _mapId: string): Promise<string[]> {
  const { data: nodeRow } = await (supabase as any)
    .from("thematic_nodes")
    .select("primary_entity_id")
    .eq("id", nodeId)
    .maybeSingle();
  if (!nodeRow?.primary_entity_id) return [];

  const { data: eavRows } = await (supabase as any)
    .from("thematic_entity_attributes")
    .select("attribute_name, value")
    .eq("entity_id", nodeRow.primary_entity_id)
    .limit(15);
  return (eavRows || []).map((r: any) => String(r.value));
}

// v8: Programmatic anchor validation — area.name MUSI contain anchor_value as substring
// + anchor_value MUSI exist in declared pool + uniqueness (no anchor reuse).
const MANDATORY_ANCHOR_TAGS = new Set<string>([
  "DIRECT_ANSWER", "PROVENANCE", "EVIDENCE", "BOUNDARIES", "ATTRIBUTES",
  "COMPONENTS", "MEASUREMENTS", "SIDE_BY_SIDE", "CRITERIA", "SYMPTOMS",
  "CAUSES", "SOLUTIONS", "ORIGINS", "EVOLUTION", "PRODUCT_SPECS",
  "PRICING", "TERM_DEFINITION", "COMMON_CONFUSIONS", "CORE_CONCEPTS",
  "IMPLEMENTATION_PATH",
]);

interface AreaWithAnchor {
  area: string;
  question?: string;
  ymyl?: boolean;
  anchor_source?: "KG_entity" | "EAV_attribute" | "measurable" | "fact" | "kg_relation" | null;
  anchor_value?: string | null;
}

// v9 C1: deterministic anchor_pool builder z 5-layer fallback hierarchy.
// Layer 1 LLM-validated → Layer 2 KG PE → Layer 3 KG supporting → Layer 4 measurables →
// Layer 5 EAV. Final guard: PE verbatim push gdy pool empty (always-on, NIE "if needed").
// Dedup case-insensitive, first occurrence wins (priority = layer = quality order).
// Hard cap pool.slice(0, 8) — context budget, NIE KB rule.
function buildAnchorPool(
  area: AreaWithAnchor,
  kgCtx: KgFanOutContext,
  primaryEntity: string,
): AnchorCandidate[] {
  const pool: AnchorCandidate[] = [];
  const seen = new Set<string>();
  const push = (c: AnchorCandidate) => {
    const key = c.value.toLowerCase().trim();
    if (!key || seen.has(key)) return;
    seen.add(key);
    pool.push(c);
  };

  // Layer 1: LLM-validated anchor (zachowaj nawet gdy v8 final_valid=false — best-effort partial)
  if (area.anchor_value && area.anchor_source) {
    const typeMap: Record<string, AnchorType> = {
      KG_entity: "entity",
      kg_relation: "entity",
      EAV_attribute: "fact",
      measurable: "measurable",
      fact: "fact",
    };
    push({
      value: area.anchor_value,
      type: typeMap[area.anchor_source] || "entity",
      source: "llm",
    });
  }

  // Layer 2: KG primary_entity (top by strength — index 0)
  if (kgCtx.kgEntityNamesPool[0]) {
    push({ value: kgCtx.kgEntityNamesPool[0], type: "entity", source: "kg_pe" });
  }

  // Layer 3: KG supporting (top 3 after PE, dedup auto przez seen)
  for (const name of kgCtx.kgEntityNamesPool.slice(1, 4)) {
    push({ value: name, type: "entity", source: "kg_supporting" });
  }

  // Layer 4: per-node measurables (top 3)
  for (const meas of kgCtx.measurableSummaries.slice(0, 3)) {
    push({ value: meas, type: "measurable", source: "eav" });
  }

  // Layer 5: EAV fallback values (per-PE scoped via fetchEavPerPrimaryEntityFallback OR ctxEav)
  for (const eav of (kgCtx.eavValuesPool || []).slice(0, 3)) {
    push({ value: eav, type: "fact", source: "map_fallback" });
  }

  // Final guard: pool empty edge case → PE verbatim push (always-on)
  if (pool.length === 0 && primaryEntity) {
    pool.push({ value: primaryEntity, type: "entity", source: "kg_pe" });
  }

  return pool.slice(0, 8); // hard cap context budget
}

function validateAreaNameAnchoring(
  areas: AreaWithAnchor[],
  kgEntityNamesPool: string[],
  eavValuesPool: string[],
  measurableSummaries: string[],
): { valid: boolean; failures: Array<{ area: string; reason: string }> } {
  const failures: Array<{ area: string; reason: string }> = [];
  const seenAnchorValues = new Set<string>();
  const kgLower = new Set(kgEntityNamesPool.map((s) => s.toLowerCase()));
  const eavLower = eavValuesPool.map((s) => s.toLowerCase());
  const measLower = measurableSummaries.map((s) => s.toLowerCase());

  for (const a of areas) {
    const tagMatch = a.area.match(/^\[([A-Z_]+)\]/);
    const tag = tagMatch ? tagMatch[1] : null;
    const mandatory = !!(tag && MANDATORY_ANCHOR_TAGS.has(tag));

    if (!mandatory && (!a.anchor_source || a.anchor_source === null)) continue;

    if (mandatory && (!a.anchor_source || a.anchor_source === null)) {
      failures.push({ area: a.area, reason: `tag [${tag}] requires anchor_source (KB §4.2 verifiable detail)` });
      continue;
    }

    const value = String(a.anchor_value || "").toLowerCase().trim();
    if (!value) {
      failures.push({ area: a.area, reason: "anchor_source set but anchor_value empty" });
      continue;
    }

    if (seenAnchorValues.has(value)) {
      failures.push({ area: a.area, reason: `anchor_value "${a.anchor_value}" reused across areas — each area MUST have unique anchor` });
      continue;
    }
    seenAnchorValues.add(value);

    let foundInPool = false;
    if (a.anchor_source === "KG_entity" || a.anchor_source === "kg_relation") {
      foundInPool = Array.from(kgLower).some((e) => e === value || value.includes(e) || e.includes(value));
    } else if (a.anchor_source === "EAV_attribute") {
      foundInPool = eavLower.some((v) => v.includes(value) || value.includes(v));
    } else if (a.anchor_source === "measurable") {
      foundInPool = measLower.some((m) => m.includes(value) || value.includes(m));
    } else if (a.anchor_source === "fact") {
      // Facts are free-text — accept any non-empty value (deeper validation would need fact-text Map).
      foundInPool = true;
    }

    if (!foundInPool) {
      failures.push({ area: a.area, reason: `anchor_value "${a.anchor_value}" not in declared ${a.anchor_source} pool` });
      continue;
    }

    if (!a.area.toLowerCase().includes(value)) {
      failures.push({ area: a.area, reason: `area.name does not contain anchor_value "${a.anchor_value}" as substring` });
    }
  }

  return { valid: failures.length === 0, failures };
}

// =============================================
// Types
// =============================================

export interface IntentArea {
  type: string;
  description: string;
  micro_areas: string[];
}

export interface DefinitionalArea {
  term: string;
  expected_content: string;
}

// v9 C1: Anchor candidates per micro_area for downstream outline LLM + Rule 15 judge.
// Built deterministically z fallback hierarchy (LLM-validated → KG PE → KG supporting →
// per-node measurables → EAV). ALWAYS min 1 element (final guard = PE verbatim push).
// KB SRO §2.5 Entity Anchoring + §4.3 Supporting Entities.
export type AnchorType = "entity" | "measurable" | "temporal" | "fact";
export type AnchorSource = "llm" | "kg_pe" | "kg_supporting" | "eav" | "map_fallback";
export interface AnchorCandidate {
  value: string;        // pure text — downstream LLM widzi tylko to
  type: AnchorType;     // forensics only
  source: AnchorSource; // forensics only
}

export interface MicroArea {
  order: number;
  intent: string;
  area: string;
  question: string;
  paa_questions: string[];
  ymyl: boolean;
  anchor_pool: AnchorCandidate[]; // v9 C1: ALWAYS min 1 element (final guard)
}

export interface FanOut {
  primary_intent: IntentArea;
  secondary_intents: IntentArea[];
  definitional_areas: DefinitionalArea[];
  paa_questions: string[];
  ymyl: boolean;
  micro_areas: MicroArea[];
}

export interface FanOutContext {
  seed_intent_family?: string[];
  macro_statement?: string;
  boundaries_not_owned?: string[];
  market_keywords_top5?: Array<{ keyword: string; search_volume: number }>;
}

// =============================================
// Closed taxonomy + role mapping
// =============================================

const VALID_INTENTS = new Set([
  "Definicyjna", "Problemowa", "Instrukcyjna",
  "Decyzyjna", "Diagnostyczna", "Porownawcza",
]);

const ROLE_TO_PRIMARY_INTENT: Record<string, string> = {
  definition:      "Definicyjna",
  glossary:        "Definicyjna",
  history:         "Definicyjna",
  how_to:          "Instrukcyjna",
  protocol:        "Instrukcyjna",
  guide:           "Instrukcyjna",
  comparison:      "Porownawcza",
  troubleshooting: "Problemowa",
  commercial:      "Decyzyjna",
};

// Recovery mapping: jesli LLM zwroci SRO Passage Tag jako intent label
// (zamiast PL intent enum), zmapuj na poprawna intent. Powod: LLM czesto
// myli warstwy — SRO tag to PREFIX area.name (np. "[SOLUTIONS] Strategie..."),
// nie intent. Bez recovery cale areas z tego "intent" by ginely.
//
// Smoke test 14:38 node 1 (troubleshooting): LLM zwrocil intents
// ["Problemowa", "SOLUTIONS", "PREVENTION"] — bez fixa SOLUTIONS i PREVENTION
// wycinane → outline bez H2 [SOLUTIONS] (krytyczna luka dla troubleshooting).
const SRO_TAG_TO_INTENT: Record<string, string> = {
  // Definicyjna — opisuje czym jest, atrybuty, granice, sklad
  DIRECT_ANSWER:   "Definicyjna",
  TERM_DEFINITION: "Definicyjna",
  BOUNDARIES:      "Definicyjna",
  ATTRIBUTES:      "Definicyjna",
  COMPONENTS:      "Definicyjna",
  USAGE_CONTEXT:   "Definicyjna",
  // Problemowa — objawy + przyczyny
  SYMPTOMS:        "Problemowa",
  CAUSES:          "Problemowa",
  // Diagnostyczna — jak sprawdzic, weryfikacja, evidence, typowe bledy
  VERIFICATION:    "Diagnostyczna",
  EVIDENCE:        "Diagnostyczna",
  COMMON_MISTAKES: "Diagnostyczna",
  // Instrukcyjna — jak zrobic, rozwiazania, kroki, zapobieganie
  SOLUTIONS:       "Instrukcyjna",
  PREVENTION:      "Instrukcyjna",
  STEPS:           "Instrukcyjna",
  USE_CASES:       "Instrukcyjna",
  // Porownawcza — side-by-side
  SIDE_BY_SIDE:    "Porownawcza",
  // Decyzyjna — kryteria wyboru
  CRITERIA:        "Decyzyjna",
};

/** Role-specific area constraints — what kind of areas should be generated */
const ROLE_AREA_HINTS: Record<string, string> = {
  history: `Rola = history. Generuj areas o ujeciu HISTORYCZNYM encji:
- poczatki i ewolucja (geneza + jak sie zmienial w czasie — JEDEN area, nie dwa!)
- wplyw regionow / kultur / wydarzen historycznych
- znaczenie kulturowe i spoleczne w historii
- stan obecny vs przeszlosc (jak wyglada dzis w porownaniu)
WAZNE: NIE rozdzielaj chronologii, genezy i ewolucji na osobne areas — to jest JEDEN temat!
NIE generuj ogolnych areas typu: "co to jest", "rodzaje", "zastosowania", "skladniki" (chyba ze w kontekscie historycznym).
ANTY-OVERLAP: NIE generuj 2+ areas ktore wymagalyby pisania o tych samych faktach. Test: czy autor moglby napisac oba H2 bez powtorzenia informacji? Jesli NIE → polacz.`,
  definition: `Rola = definition. Generuj areas o:
- definicja / czym jest
- cechy / atrybuty
- skladniki / komponenty
- warianty / typy
- zastosowanie / kontekst uzycia
NIE generuj areas o: popularnosci, historii, trendach, porownaniach.
ANTY-OVERLAP: NIE generuj 2+ areas ktore wymagalyby pisania o tych samych faktach. Test: czy autor moglby napisac oba H2 bez powtorzenia informacji? Jesli NIE → polacz.`,
  how_to: `Rola = how_to. Generuj areas o:
- kroki / procedura
- materialy / narzedzia / skladniki
- typowe bledy / pulapki
- wskazowki / best practices
NIE generuj areas o: pelna definicja, historia, porownania.
ANTY-OVERLAP: NIE generuj 2+ areas ktore wymagalyby pisania o tych samych faktach. Test: czy autor moglby napisac oba H2 bez powtorzenia informacji? Jesli NIE → polacz.`,
  comparison: `Rola = comparison. Generuj areas o:
- kluczowe roznice
- tabela porownawcza / cechy
- kiedy co wybrac / scenariusze
- zalety i wady kazdej opcji
NIE generuj areas o: pelne definicje obu stron.
ANTY-OVERLAP: NIE generuj 2+ areas ktore wymagalyby pisania o tych samych faktach. Test: czy autor moglby napisac oba H2 bez powtorzenia informacji? Jesli NIE → polacz.`,
  troubleshooting: `Rola = troubleshooting. Generuj areas o:
- objawy / jak rozpoznac problem
- przyczyny / zrodla
- rozwiazania krok po kroku
- zapobieganie
NIE generuj areas o: definicja, historia.
ANTY-OVERLAP: NIE generuj 2+ areas ktore wymagalyby pisania o tych samych faktach. Test: czy autor moglby napisac oba H2 bez powtorzenia informacji? Jesli NIE → polacz.`,
  commercial: `Rola = commercial. Generuj areas o:
- cechy produktu / co oferuje
- porownanie opcji / alternatywy
- cena / gdzie kupic
- opinie / doswiadczenia
NIE generuj areas o: pelna definicja, historia.
ANTY-OVERLAP: NIE generuj 2+ areas ktore wymagalyby pisania o tych samych faktach. Test: czy autor moglby napisac oba H2 bez powtorzenia informacji? Jesli NIE → polacz.`,
};

const AREAS_SOFT_CAP = 8;
const AREAS_HARD_MAX = 12;
const MAX_AREAS_PER_INTENT = 5;
const MAX_INTENTS = 4; // primary + max 3 secondary

// =============================================
// Main function
// =============================================

export async function generateFanOut(
  nodeId: string,
  query: string,
  mapId: string,
  documentRole: string,
  primaryEntity: string,
  language: string,
  ctxEav?: Array<{ entity_name: string; attribute_name: string; value: string }>,
  fanOutCtx?: FanOutContext,
  kgIdeations?: Array<{ ideation?: string; description?: string }>,
): Promise<{ fanOut: FanOut; costUsd: number; debug: Record<string, unknown> }> {

  // 0. Determine primary intent from document_role (hard mapping)
  const forcedPrimaryIntent = ROLE_TO_PRIMARY_INTENT[documentRole] || "Definicyjna";
  const roleHint = ROLE_AREA_HINTS[documentRole] || "";

  // 1. Fetch PAA questions (used in Call 2, NOT in Call 1)
  let paaQuestions: string[] = [];
  try {
    paaQuestions = await getPeopleAlsoAsk(query);
  } catch {
    // PAA is optional
  }

  // 1b. Dedup PAA: Jaccard > 0.6 → keep longer one, skip duplicate
  const preDedupCount = paaQuestions.length;
  const dedupedPaa: string[] = [];
  for (const paa of paaQuestions) {
    const paaWords = new Set(paa.toLowerCase().replace(/[?!.]/g, "").split(/\s+/).filter(w => w.length > 2));
    const isDup = dedupedPaa.some(existing => {
      const exWords = new Set(existing.toLowerCase().replace(/[?!.]/g, "").split(/\s+/).filter(w => w.length > 2));
      const inter = [...paaWords].filter(w => exWords.has(w)).length;
      const union = new Set([...paaWords, ...exWords]).size;
      return union > 0 && inter / union > 0.6;
    });
    if (!isDup) dedupedPaa.push(paa);
  }
  paaQuestions = dedupedPaa;
  if (preDedupCount !== paaQuestions.length) {
    logDebug("FanOut", `PAA dedup: ${preDedupCount} → ${paaQuestions.length}`);
  }

  // 2. EAV context
  let eavContext: string;
  if (ctxEav) {
    eavContext = ctxEav.map((a) => `${a.attribute_name}: ${a.value}`).join(", ");
  } else {
    const { data: eavAttrs } = await (supabase as any)
      .from("thematic_entity_attributes")
      .select("attribute_name, value")
      .eq("map_id", mapId)
      .limit(10);
    eavContext = (eavAttrs || []).map((a: any) => `${a.attribute_name}: ${a.value}`).join(", ");
  }

  // v8: KG context fetch (entities + measurables + facts + relations) — per-node
  const kgCtx = await fetchKgContextForFanOut(nodeId);

  // v8: EAV values pool — ctxEav PREFERRED (per-node pre-filtered), FALLBACK per-PE via primary_entity_id JOIN
  // (NIE map_id jak v7 broken — fix dla cross-entity leakage)
  let eavValuesPool: string[] = [];
  if (ctxEav && ctxEav.length > 0) {
    eavValuesPool = ctxEav.map((r) => String(r.value));
  } else {
    eavValuesPool = await fetchEavPerPrimaryEntityFallback(nodeId, mapId);
  }
  kgCtx.eavValuesPool = eavValuesPool;

  // Context strings
  const marketKwStr = (fanOutCtx?.market_keywords_top5 || [])
    .map((k) => `"${k.keyword}" (SV=${k.search_volume})`)
    .join(", ") || "brak";
  const boundariesStr = (fanOutCtx?.boundaries_not_owned || []).slice(0, 10).join(", ") || "brak";
  const seedHintStr = (fanOutCtx?.seed_intent_family || []).length > 0
    ? `Cel edytorski seeda: ${(fanOutCtx!.seed_intent_family!).join(", ")}. Wykorzystaj jako hint do priorytetyzacji areas, ale intencje MUSZA byc z zamknietej listy.`
    : "";

  let totalCost = 0;

  // ========================================
  // CALL 1: Intent & Areas (BEZ PAA na wejściu)
  // ========================================

  const dbPrompt1 = await loadPrompt("fan_out");
  const stageConfig1 = getStageConfig("fan_out");
  const model1 = dbPrompt1?.modelId || stageConfig1.model;
  const provider1 = dbPrompt1?.provider || stageConfig1.provider;

  const templateVars1: Record<string, string> = {
    query, keyword: query,
    document_role: documentRole,
    primary_entity: primaryEntity,
    language, language_name: language,
    eav_context: eavContext || "brak",
    forced_primary_intent: forcedPrimaryIntent,
    role_area_hint: roleHint,
    seed_hint: seedHintStr,
    macro_statement: fanOutCtx?.macro_statement || "",
    market_keywords: marketKwStr,
    boundaries_not_owned: boundariesStr,
    // PAA NOT included in Call 1 — separate step
    paa_questions: "brak danych",
    seed_intent_family: (fanOutCtx?.seed_intent_family || []).join(", ") || "brak",
    kg_ideations: (kgIdeations || [])
      .map((i) => "- " + (i.ideation || i.description || ""))
      .filter((s) => s.length > 3)
      .join("\n") || "(brak)",
    // v8 NEW: KG context pools for anchored area generation
    kg_entities_summary: kgCtx.kgEntitiesSummary,
    kg_measurables: kgCtx.kgMeasurables,
    kg_facts: kgCtx.kgFacts,
    kg_relations: kgCtx.kgRelations,
  };

  const prompt1 = dbPrompt1
    ? fillTemplate(dbPrompt1.template, templateVars1)
    : buildCall1Fallback(query, primaryEntity, documentRole, forcedPrimaryIntent, roleHint, seedHintStr, eavContext, marketKwStr, boundariesStr, language, fanOutCtx?.macro_statement);

  logPublic("FanOut", `Call 1: Intent & Areas (${provider1}/${model1})`);
  logDebug("FanOut", "Call 1 config", {
    step: "fan_out", provider: provider1, model: model1,
    template_loaded: !!dbPrompt1, prompt_length: prompt1.length,
    document_role: documentRole, primary_entity: primaryEntity,
    forced_primary_intent: forcedPrimaryIntent,
    role_hint: roleHint ? "yes" : "none",
    boundaries: boundariesStr !== "brak" ? "yes" : "none",
  });
  logVerbose("FanOut", `Call 1 FULL PROMPT:\n${prompt1}`);

  const start1 = Date.now();
  const response1 = await getLLMProvider(provider1).call(prompt1, {
    model: model1, temperature: dbPrompt1?.temperature ?? 0.3,
    step: "fan_out", mapId, nodeId,
  });
  const duration1 = Date.now() - start1;
  totalCost += response1.costUsd || 0;

  let intentResult: any = parseLLMJSON<any>(response1.text);

  // v8: Anchor validation retry loop — flatten areas, validate, retry with feedback up to MAX times.
  // KAZDA mandatory-SRO-tag area MUSI miec anchor_source + anchor_value w pool.
  // BUG ROOT CAUSE (recon §A): Fan-Out v7 templates ("Geneza {{PE}}" itd.) reinforced PE-stuffed
  //   area.name generation, propagating through outline Rule 1 verbatim copy → cross-mapa fingerprint.
  // v8 fix: validate output post-LLM, retry with explicit pool feedback gdy anchor missing/invalid.
  const MAX_FAN_OUT_ANCHOR_RETRIES = 2;
  let anchorAttempts = 0;
  let anchorValidation: ReturnType<typeof validateAreaNameAnchoring> | null = null;
  let failuresBeforeRetry: Array<{ area: string; reason: string }> = [];
  let totalAnchorRetryCost = 0;

  while (anchorAttempts <= MAX_FAN_OUT_ANCHOR_RETRIES) {
    const allAreasFlat: AreaWithAnchor[] = [];
    for (const intent of (intentResult.intents || [])) {
      for (const area of (intent.areas || [])) allAreasFlat.push(area);
    }

    anchorValidation = validateAreaNameAnchoring(
      allAreasFlat,
      kgCtx.kgEntityNamesPool,
      eavValuesPool,
      kgCtx.measurableSummaries,
    );

    if (anchorAttempts === 0) failuresBeforeRetry = [...anchorValidation.failures];
    if (anchorValidation.valid) {
      if (anchorAttempts > 0) logPublic("FanOut", `v8 anchor validation PASS after ${anchorAttempts} retry`);
      break;
    }
    if (anchorAttempts >= MAX_FAN_OUT_ANCHOR_RETRIES) {
      logPublic("FanOut", `v8 anchor_violation_after_retry — ${anchorValidation.failures.length} areas with invalid/missing anchors (soft fallback, accept best attempt)`);
      logDebug("FanOut", "v8 anchor violations final", anchorValidation.failures);
      break;
    }

    anchorAttempts++;
    const feedbackLines = anchorValidation.failures
      .map((f) => `- ${f.area} → ${f.reason}`)
      .join("\n");
    const retryFeedback = `\n\n# RETRY FEEDBACK (próba ${anchorAttempts}/${MAX_FAN_OUT_ANCHOR_RETRIES})\n\nPoprzedni output ma ${anchorValidation.failures.length} area(s) bez walidnego anchora:\n${feedbackLines}\n\nDOSTĘPNE pools dla anchorowania:\n## KG entities (top 15):\n${kgCtx.kgEntitiesSummary}\n## Measurables:\n${kgCtx.kgMeasurables}\n## High-priority facts:\n${kgCtx.kgFacts}\n## EAV values (pierwsze 10): ${eavValuesPool.slice(0, 10).join(", ") || "(brak)"}\n\nWygeneruj NOWY output z anchored area.name. Każde area z mandatory SRO tag MUSI mieć anchor_source + anchor_value pochodzący z powyższego pool. area.name MUSI zawierać anchor_value jako substring. ZERO template phrasings ("Definicja X" / "Geneza X" / "Cechy X").`;

    logPublic("FanOut", `v8 anchor retry ${anchorAttempts}/${MAX_FAN_OUT_ANCHOR_RETRIES} — ${anchorValidation.failures.length} flagged`);
    const retryRes = await getLLMProvider(provider1).call(prompt1 + retryFeedback, {
      model: model1, temperature: dbPrompt1?.temperature ?? 0.3,
      step: "fan_out", mapId, nodeId,
    });
    totalAnchorRetryCost += retryRes.costUsd || 0;
    totalCost += retryRes.costUsd || 0;
    intentResult = parseLLMJSON<any>(retryRes.text);
  }

  const rawIntents: any[] = intentResult.intents || [];

  // POST-CALL 1: Validate intents against closed enum
  // Z recovery: jesli LLM zwroci SRO Passage Tag (np. "SOLUTIONS") jako intent,
  // zmapuj na PL intent (np. "Instrukcyjna") zamiast wycinac. Areas merge do
  // istniejacego intent jesli juz mamy walidowany z ta sama nazwa.
  const validatedIntents: any[] = [];
  const removedIntents: string[] = [];
  const recoveredIntents: Array<{ original: string; recovered: string; areas: number }> = [];
  for (const intent of rawIntents) {
    const name = intent.intent || intent.nazwa || "";
    if (VALID_INTENTS.has(name)) {
      // Trim areas to max per intent
      intent.areas = (intent.areas || []).slice(0, MAX_AREAS_PER_INTENT);
      validatedIntents.push(intent);
      continue;
    }
    // RECOVERY: SRO Passage Tag jako intent → mapuj na PL intent
    const recoveredName = SRO_TAG_TO_INTENT[name];
    if (recoveredName) {
      const incomingAreas = intent.areas || [];
      recoveredIntents.push({ original: name, recovered: recoveredName, areas: incomingAreas.length });
      // Merge do istniejacego validIntent o tej samej recovered name LUB push nowy
      const existing = validatedIntents.find((i) => (i.intent || i.nazwa) === recoveredName);
      if (existing) {
        existing.areas = [...(existing.areas || []), ...incomingAreas].slice(0, MAX_AREAS_PER_INTENT);
      } else {
        intent.intent = recoveredName;
        intent.areas = incomingAreas.slice(0, MAX_AREAS_PER_INTENT);
        validatedIntents.push(intent);
      }
      continue;
    }
    // Genuinely invalid → wycinaj (legacy behavior)
    removedIntents.push(name);
  }
  if (recoveredIntents.length > 0) {
    logPublic(
      "FanOut",
      `intent_recovered_from_sro_tag: ${recoveredIntents.length} (${recoveredIntents.map((r) => `${r.original}→${r.recovered} +${r.areas} areas`).join("; ")})`,
    );
  }

  // Ensure forced primary intent exists
  if (!validatedIntents.some((i) => i.intent === forcedPrimaryIntent)) {
    validatedIntents.unshift({ intent: forcedPrimaryIntent, areas: [] });
  }

  // Limit to MAX_INTENTS (primary + 3 secondary)
  const primaryIdx = validatedIntents.findIndex((i) => i.intent === forcedPrimaryIntent);
  const primary = primaryIdx >= 0 ? validatedIntents.splice(primaryIdx, 1)[0] : validatedIntents.shift()!;
  const secondary = validatedIntents.slice(0, MAX_INTENTS - 1);

  logPublic("FanOut", `Call 1 result: ${rawIntents.length} intents, ${[primary, ...secondary].flatMap(i => i.areas || []).length} areas, primary=${forcedPrimaryIntent}`);
  logDebug("FanOut", "Call 1 details", {
    raw_intents: rawIntents.length,
    validated: validatedIntents.length + 1,
    removed_intents: removedIntents,
    primary_intent: forcedPrimaryIntent,
    areas_per_intent: [primary, ...secondary].map((i) => `${i.intent}: ${(i.areas || []).length}`),
    tokens_in: response1.tokensInput, tokens_out: response1.tokensOutput,
    cost: response1.costUsd?.toFixed(4), duration_ms: duration1,
  });

  // ========================================
  // CALL 2: PAA Assignment (NEW — separate step)
  // ========================================

  const allAreas = [primary, ...secondary].flatMap((i) =>
    (i.areas || []).map((a: any) => ({ intent: i.intent, area: a.area || a.temat || "", question: a.question || a.pytanie || "" }))
  );

  let paaAssignments: Record<string, string[]> = {};
  let unmatchedPaa: string[] = [];
  let cost2 = 0;
  let duration2 = 0;

  if (paaQuestions.length > 0 && allAreas.length > 0) {
    const dbPrompt2 = await loadPrompt("fan_out_paa_assignment");
    const stageConfig2 = getStageConfig("fan_out_paa_assignment" as any);
    const model2 = dbPrompt2?.modelId || stageConfig2?.model || "gpt-4.1-mini";
    const provider2 = dbPrompt2?.provider || stageConfig2?.provider || "openai";

    const areasListStr = allAreas.map((a, i) => `${i + 1}. [${a.intent}] ${a.area}: "${a.question}"`).join("\n");
    const paaListStr = paaQuestions.map((q, i) => `${i + 1}. ${q}`).join("\n");

    const templateVars2: Record<string, string> = {
      keyword: query, primary_entity: primaryEntity,
      areas_list: areasListStr, paa_list: paaListStr,
    };

    const prompt2 = dbPrompt2
      ? fillTemplate(dbPrompt2.template, templateVars2)
      : `Przypisz kazde pytanie PAA do NAJLEPIEJ pasujacego obszaru tematycznego.

# Zasady:
- Kazde PAA przypisz do JEDNEGO obszaru (najlepsze dopasowanie)
- Jesli PAA jest zbyt ogolne lub nie pasuje do zadnego obszaru → dodaj do "unmatched"
- NIE twórz nowych obszarow na podstawie PAA
- PAA musi byc SPECYFICZNE dla danego obszaru, nie ogolne

# Obszary:
${areasListStr}

# Pytania PAA:
${paaListStr}

# Keyword: ${query}

Zwroc JSON:
{
  "assignments": { "Nazwa obszaru": ["PAA 1", "PAA 2"] },
  "unmatched": ["PAA ktore nie pasuja"]
}`;

    logPublic("FanOut", `Call 2: PAA Assignment (${allAreas.length} areas, ${paaQuestions.length} PAA)`);
    logDebug("FanOut", "Call 2 config", {
      step: "fan_out_paa_assignment", provider: provider2, model: model2,
      template_loaded: !!dbPrompt2, areas_count: allAreas.length, paa_count: paaQuestions.length,
    });

    const start2 = Date.now();
    const response2 = await getLLMProvider(provider2).call(prompt2, {
      model: model2, temperature: 0.2,
      step: "fan_out_paa_assignment", mapId, nodeId,
    });
    duration2 = Date.now() - start2;
    cost2 = response2.costUsd || 0;
    totalCost += cost2;

    const paaResult = parseLLMJSON<any>(response2.text);
    paaAssignments = paaResult.assignments || paaResult.przypisania || {};
    unmatchedPaa = paaResult.unmatched || paaResult.niepasujace || paaResult.niepasujace_paa || [];

    logPublic("FanOut", `Call 2 result: ${Object.values(paaAssignments).reduce((s: number, v: any) => s + (Array.isArray(v) ? v.length : 0), 0)} assigned, ${unmatchedPaa.length} unmatched`);
    logDebug("FanOut", "Call 2 PAA details", {
      assigned: Object.values(paaAssignments).reduce((s: number, v: any) => s + (Array.isArray(v) ? v.length : 0), 0),
      unmatched: unmatchedPaa.length,
      unmatched_list: unmatchedPaa,
      assignments: Object.entries(paaAssignments).map(([area, paas]) => `${area}: ${(paas as string[]).length} PAA`),
      tokens_in: response2.tokensInput, tokens_out: response2.tokensOutput,
      cost: cost2.toFixed(4), duration_ms: duration2,
    });
  } else {
    logPublic("FanOut", `Call 2 SKIPPED: paa=${paaQuestions.length}, areas=${allAreas.length}`);
  }

  // ========================================
  // CALL 3: MICRO/MACRO Classification (with context)
  // ========================================

  const dbPrompt3 = await loadPrompt("fan_out_classification");
  const stageConfig3 = getStageConfig("fan_out_classification");
  const model3 = dbPrompt3?.modelId || stageConfig3.model;
  const provider3 = dbPrompt3?.provider || stageConfig3.provider;

  // Build enriched input for classification (includes role + boundaries)
  const classInput = {
    query, primary_entity: primaryEntity, document_role: documentRole,
    primary_intent: forcedPrimaryIntent,
    intents: [primary, ...secondary],
  };

  const templateVars3: Record<string, string> = {
    fan_out_result: JSON.stringify(classInput, null, 2),
    document_role: documentRole,
    primary_entity: primaryEntity,
    boundaries_not_owned: boundariesStr,
  };

  const prompt3 = dbPrompt3
    ? fillTemplate(dbPrompt3.template, templateVars3)
    : `Sklasyfikuj kazdy obszar jako MICRO (sekcja w artykule) lub MACRO (osobny artykul).

# Test samodzielnosci:
"Czy uzytkownik moglby wpisac to jako OSOBNE zapytanie i oczekiwac OSOBNEJ, pelnej odpowiedzi?"
TAK = MACRO (osobny artykul, usun kontekst specyficzny z query)
NIE = MICRO (sekcja w artykule glownym, zachowaj kontekst)

# Kontekst:
- Query: "${query}"
- Rola dokumentu: ${documentRole}
- Glowna encja: ${primaryEntity}
- Tematy POZA SCOPE: ${boundariesStr}

# Input:
${JSON.stringify(classInput, null, 2)}

Zwroc JSON:
{
  "primary_intent": "${forcedPrimaryIntent}",
  "micro_areas": [{ "area": "...", "question": "...", "ymyl": false, "source_intent": "..." }],
  "macro_areas": [{ "area": "...", "question": "...", "ymyl": false, "source_intent": "..." }]
}`;

  logPublic("FanOut", `Call 3: Classification (${provider3}/${model3})`);
  logDebug("FanOut", "Call 3 config", {
    step: "fan_out_classification", provider: provider3, model: model3,
    template_loaded: !!dbPrompt3, prompt_length: prompt3.length,
  });

  const start3 = Date.now();
  const response3 = await getLLMProvider(provider3).call(prompt3, {
    model: model3, temperature: dbPrompt3?.temperature ?? 0.3,
    step: "fan_out_classification", mapId, nodeId,
  });
  const duration3 = Date.now() - start3;
  totalCost += response3.costUsd || 0;

  const classResult = parseLLMJSON<any>(response3.text);
  let microAreas: any[] = Array.isArray(classResult.micro_areas) ? classResult.micro_areas : [];
  const macroAreas: any[] = Array.isArray(classResult.macro_areas) ? classResult.macro_areas : [];

  // POST-CALL 3: Enforce limits — soft cap 8, hard max 12
  if (microAreas.length > AREAS_SOFT_CAP) {
    // Prioritize: primary intent first, then best-matching secondary
    const primaryMicro = microAreas.filter((m: any) => (m.source_intent || m.intent) === forcedPrimaryIntent);
    const secondaryMicro = microAreas.filter((m: any) => (m.source_intent || m.intent) !== forcedPrimaryIntent);
    microAreas = [...primaryMicro, ...secondaryMicro].slice(0, AREAS_HARD_MAX);
    logPublic("FanOut", `Trimmed micro areas: ${primaryMicro.length} primary + ${secondaryMicro.length} secondary → ${microAreas.length} total`);
  }

  logPublic("FanOut", `Call 3 result: ${microAreas.length} micro, ${macroAreas.length} macro`);
  logDebug("FanOut", "Call 3 details", {
    micro: microAreas.length, macro: macroAreas.length,
    primary_intent: forcedPrimaryIntent,
    micro_list: microAreas.map((m: any) => `[${m.source_intent || m.intent || "?"}] ${m.area}`),
    macro_list: macroAreas.map((m: any) => m.area),
    tokens_in: response3.tokensInput, tokens_out: response3.tokensOutput,
    cost: response3.costUsd?.toFixed(4), duration_ms: duration3,
  });

  // ========================================
  // PAA fuzzy match helper (Jaccard similarity)
  // ========================================
  //
  // P5 v13c fix (analog outline P1+P3): Call 2 LLM zwraca keys w 3 formach mimo
  // promptu Fan-Out v4 ktory wymaga "DOKLADNIE nazwy area BEZ prefixu intencji":
  //   1. "[Definicyjna] [TAG] X" (prefix intent doklejony)
  //   2. "[TAG] X: \"Question?\"" (trailing PAA suffix)
  //   3. Mix obu: "[Definicyjna] [TAG] X: \"Question?\""
  // Stary cleanKey = `key.split(":")[0]` byl destruktywny — gubił v8 anchor:context
  // suffix po internal `:` (np. "First Round 2015: kanoniczny opis" → "First Round 2015").
  // Empirycznie potwierdzone w logach 7a3aea74 (100% no match) + 04fc144e (4/5 no match).
  //
  // Fix: normalizeAreaKey strip intent prefix + trailing PAA suffix (analog P1 regex
  // z extended quote class PL/DE/JP curly quotes U+201C/D/E).
  const INTENT_PREFIX_RE = /^\s*\[(Definicyjna|Problemowa|Instrukcyjna|Decyzyjna|Diagnostyczna|Porownawcza)\]\s+/i;
  const PAA_SUFFIX_RE = /\s*:\s*["'“”„][^"'“”„]*["'“”„]?\s*$/;
  const TRIM_QUOTES_RE = /^["'`“”„\s]+|["'`“”„\s]+$/g;

  function normalizeAreaKey(s: string): string {
    return s
      .replace(INTENT_PREFIX_RE, "")
      .replace(PAA_SUFFIX_RE, "")
      .replace(TRIM_QUOTES_RE, "")
      .trim();
  }

  function findPaaForArea(areaName: string, assignments: Record<string, string[]>): string[] {
    const areaCanonical = normalizeAreaKey(areaName);

    // Try direct exact lookup on normalized key
    if (assignments[areaCanonical]?.length > 0) {
      logDebug("FanOut", `PAA exact match (normalized): "${areaName}" → ${assignments[areaCanonical].length} PAA`);
      return assignments[areaCanonical];
    }
    // Also try original key (legacy fallback)
    if (assignments[areaName]?.length > 0) {
      logDebug("FanOut", `PAA exact match (raw): "${areaName}" → ${assignments[areaName].length} PAA`);
      return assignments[areaName];
    }

    const areaNorm = normalizeForDedup(areaCanonical);

    for (const [key, paas] of Object.entries(assignments)) {
      if (!paas || paas.length === 0) continue;
      const cleanKey = normalizeAreaKey(key);
      if (cleanKey === areaCanonical || normalizeForDedup(cleanKey) === areaNorm) {
        logDebug("FanOut", `PAA clean-key match: "${areaName}" ← raw key "${key}" → cleaned "${cleanKey}" (${paas.length} PAA)`);
        return paas;
      }
    }

    // Fallback: Jaccard on cleaned keys
    let bestMatch: string[] = [];
    let bestScore = 0;
    let bestCleanKey = "";

    for (const [key, paas] of Object.entries(assignments)) {
      if (!paas || paas.length === 0) continue;
      const cleanKey = normalizeAreaKey(key);
      const keyNorm = normalizeForDedup(cleanKey);
      const jaccard = jaccardSimilarity(areaNorm, keyNorm);

      if (jaccard > bestScore && jaccard >= 0.5) {
        bestScore = jaccard;
        bestMatch = paas;
        bestCleanKey = cleanKey;
      }
    }

    if (bestMatch.length > 0) {
      logDebug("FanOut", `PAA fuzzy match: "${areaName}" ← "${bestCleanKey}" (jaccard=${bestScore.toFixed(2)}, ${bestMatch.length} PAA)`);
    } else {
      logDebug("FanOut", `PAA no match: "${areaName}" → 0 PAA`);
    }
    return bestMatch;
  }

  // ========================================
  // BUILD FanOut structure with merged PAA
  // ========================================

  // Debug: show exact keys vs area names for PAA matching diagnosis
  logDebug("FanOut", "PAA MATCHING DEBUG", {
    paaAssignment_keys: Object.keys(paaAssignments),
    paaAssignment_values: Object.entries(paaAssignments).map(([k, v]) => `"${k}": ${(v as string[]).length} PAA`),
    microArea_names: microAreas.map((m: any) => m.area || m.name),
  });

  // Build intent lookup from Call 1 — Call 3 MUST NOT override intents
  const call1IntentLookup = new Map<string, string>();
  for (const entry of allAreas) {
    call1IntentLookup.set(normalizeForDedup(entry.area), entry.intent);
  }

  // Resolve intent for a micro area: prefer Call 1 intent, fallback to primary
  function resolveIntentFromCall1(areaName: string, llmIntent?: string): string {
    const norm = normalizeForDedup(areaName);
    // Exact match
    const exact = call1IntentLookup.get(norm);
    if (exact) return exact;
    // Fuzzy: find best Jaccard match among Call 1 areas
    let bestScore = 0;
    let bestIntent = "";
    for (const [key, intent] of call1IntentLookup.entries()) {
      const score = jaccardSimilarity(norm, key);
      if (score > bestScore) {
        bestScore = score;
        bestIntent = intent;
      }
    }
    if (bestScore >= 0.5 && bestIntent) return bestIntent;
    // Last resort: LLM said something or use primary
    return llmIntent || forcedPrimaryIntent;
  }

  // v9 C1: build areaName → AreaWithAnchor map z final intentResult (post-retry).
  // Preserves LLM-validated anchor_source/value dla Layer 1 buildAnchorPool input.
  // Klucze = area.name exactly jak LLM zwrocil; downstream lookup via areaName matche
  // microAreas Call 3 output (po RECOVERY validatedIntents may reshape, ale area.name
  // pozostaje stable identifier).
  const areaToWithAnchor = new Map<string, AreaWithAnchor>();
  for (const intent of (intentResult.intents || [])) {
    for (const a of (intent.areas || []) as AreaWithAnchor[]) {
      if (a.area) areaToWithAnchor.set(a.area, a);
    }
  }

  const fanOut: FanOut = {
    primary_intent: {
      type: forcedPrimaryIntent,
      description: intentResult.category || query,
      micro_areas: microAreas
        .filter((m: any) => resolveIntentFromCall1(m.area || m.name || "", m.source_intent || m.intent) === forcedPrimaryIntent)
        .map((m: any) => m.area || ""),
    },
    secondary_intents: secondary.map((i) => ({
      type: i.intent,
      description: i.intent,
      micro_areas: (i.areas || []).map((a: any) => a.area || ""),
    })),
    definitional_areas: [],
    paa_questions: [...unmatchedPaa],
    ymyl: microAreas.some((m: any) => m.ymyl) || false,
    micro_areas: microAreas.map((m: any, idx: number) => {
      const areaName = m.area || m.name || "";
      const resolvedIntent = resolveIntentFromCall1(areaName, m.source_intent || m.intent);
      const llmIntent = m.source_intent || m.intent || "";
      if (llmIntent && llmIntent !== resolvedIntent) {
        logPublic("FanOut", `Intent fix: "${areaName}" Call3="${llmIntent}" → Call1="${resolvedIntent}"`);
      }
      // Merge PAA from Call 2 assignments — exact match first, then Jaccard fuzzy
      const assignedPaa = findPaaForArea(areaName, paaAssignments);
      // v9 C1: lookup anchor info z Call 1 intentResult (preserves LLM-validated anchor_source/value)
      const withAnchor = areaToWithAnchor.get(areaName) ?? { area: areaName, anchor_source: null, anchor_value: null };
      return {
        order: idx + 1,
        intent: resolvedIntent,
        area: areaName,
        question: m.question || "",
        paa_questions: assignedPaa,
        ymyl: m.ymyl || false,
        anchor_pool: buildAnchorPool(withAnchor, kgCtx, primaryEntity),
      };
    }),
  };

  // Add unassigned PAA to top-level
  const allAssignedPaa = new Set(Object.values(paaAssignments).flat());
  const stillUnassigned = paaQuestions.filter((q) => !allAssignedPaa.has(q) && !unmatchedPaa.includes(q));
  fanOut.paa_questions = [...new Set([...fanOut.paa_questions, ...stillUnassigned])];

  const debug = {
    primary_intent: forcedPrimaryIntent,
    forced_from_role: documentRole,
    micro_areas: fanOut.micro_areas.length,
    macro_areas: macroAreas.length,
    intents_total: rawIntents.length,
    intents_validated: [primary, ...secondary].length,
    intents_removed: removedIntents,
    paa_total: paaQuestions.length,
    paa_assigned: Object.values(paaAssignments).reduce((s: number, v: any) => s + (Array.isArray(v) ? v.length : 0), 0),
    paa_unmatched: unmatchedPaa.length,
    areas_trimmed: microAreas.length > AREAS_SOFT_CAP,
    cost_call1: response1.costUsd || 0,
    cost_call2: cost2,
    cost_call3: response3.costUsd || 0,
    cost_usd: totalCost,
    duration_call1: duration1,
    duration_call2: duration2,
    duration_call3: duration3,
    macro_areas_detail: macroAreas,
    // v8 forensics — KG-anchored area validation
    v8_anchor_validation: {
      final_valid: anchorValidation?.valid ?? null,
      attempts: anchorAttempts,
      failures_before_retry: failuresBeforeRetry,
      failures_after_retry: anchorValidation?.failures || [],
      retry_cost_usd: totalAnchorRetryCost,
      violation_after_retry: !(anchorValidation?.valid ?? true) && anchorAttempts >= MAX_FAN_OUT_ANCHOR_RETRIES,
      pool_sizes: {
        kg_entities: kgCtx.kgEntityNamesPool.length,
        eav_values: eavValuesPool.length,
        measurables: kgCtx.measurableSummaries.length,
      },
      pe_canonical_mismatch: intentResult?.main_entity !== primaryEntity,
    },
  };

  // Save to DB
  await (supabase as any)
    .from("thematic_node_content")
    .update({ fan_out: fanOut })
    .eq("node_id", nodeId);

  logPublic("FanOut", `DONE: ${fanOut.micro_areas.length} micro, ${macroAreas.length} macro, ${debug.paa_assigned} PAA assigned`);
  logDebug("FanOut", "Final summary", {
    primary: forcedPrimaryIntent,
    micro: fanOut.micro_areas.length,
    macro: macroAreas.length,
    paa_assigned: debug.paa_assigned,
    paa_unmatched: debug.paa_unmatched,
    total_cost: totalCost.toFixed(4),
  });

  return { fanOut, costUsd: totalCost, debug };
}

// =============================================
// Fallback prompt for Call 1 (when DB prompt missing)
// =============================================

function buildCall1Fallback(
  query: string, primaryEntity: string, documentRole: string,
  forcedPrimaryIntent: string, roleHint: string, seedHint: string,
  eavContext: string, marketKwStr: string, boundariesStr: string,
  language: string, macroStatement?: string,
): string {
  return `Jestes ekspertem semantyki w jezyku ${language}.

# Kontekst node'a
- Zapytanie: "${query}"
- Glowna encja: "${primaryEntity}"
- Rola dokumentu: ${documentRole}
- Glowna intencja (z roli): ${forcedPrimaryIntent}
${macroStatement ? `- Temat mapy: ${macroStatement}` : ""}
${seedHint ? `- ${seedHint}` : ""}
${marketKwStr !== "brak" ? `- Frazy rynkowe (SERP): ${marketKwStr}` : ""}
${eavContext && eavContext !== "brak" ? `- Atrybuty encji (EAV): ${eavContext}` : ""}

# Ograniczenia scope
${boundariesStr !== "brak" ? `NIE generuj obszarow o tematach POZA SCOPE: ${boundariesStr}` : ""}

# Cel
Rozbij zapytanie na podtematy. Glowna intencja to ${forcedPrimaryIntent} (z roli ${documentRole}).

${roleHint ? `# Wskazowki dla roli\n${roleHint}\n` : ""}

# Intencje — ZAMKNIETY ENUM (TYLKO te):
- Definicyjna — czym jest, co to znaczy
- Problemowa — objawy, przyczyny, skutki problemu
- Instrukcyjna — jak zrobic, jak osiagnac
- Decyzyjna — ktory wybrac, porownanie opcji
- Diagnostyczna — jak sprawdzic, jak zmierzyc
- Porownawcza — porownanie, testy A/B

NIE wymyslaj innych intencji. NIE uzywaj nazw z seed_intent_family jako intencji.
Priorytet: ${forcedPrimaryIntent} jest glowna intencja.
Wybierz 2-4 intencje (glowna + max 3 dodatkowe), tylko istotne.

# Zasady areas:
- Max 5 areas per intencje
- Kazdy area MUSI dotyczyc "${primaryEntity}"
- Area = 2-6 slow opisujacych podtemat
- NIE twórz areas ktore sa ogolne lub nie pasuja do roli dokumentu

# Format outputu (JSON)
{
  "query": "${query}",
  "main_entity": "${primaryEntity}",
  "category": "kategoria",
  "intents": [
    {
      "intent": "${forcedPrimaryIntent}",
      "areas": [
        { "area": "Nazwa obszaru", "question": "Pytanie?", "ymyl": false }
      ]
    }
  ]
}`;
}
